import React, { useState, useMemo } from 'react';
import {
    Card, Table, Input, Select, DatePicker, Tag, Tooltip, Space, Button, Alert,
    Typography, Tabs, Row, Col, Statistic, Badge, Popconfirm, message,
} from 'antd';
import {
    ReloadOutlined, TeamOutlined, UserOutlined, WarningOutlined, LockOutlined,
    UnlockOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { useActiveUsers, useLoginHistory, useSecurityStats } from './useSessions';
import { unlockAccount } from './api';
import '../dashboard/Dashboard.css';

const { RangePicker } = DatePicker;
const { Title } = Typography;

const ONLINE_MINUTES = 5;
const RECENT_MINUTES = 30;

const activityBadge = (lastActivityAt) => {
    if (!lastActivityAt) return <Badge status="default" text="Offline" />;
    const minutes = moment().diff(moment(lastActivityAt), 'minutes');
    if (minutes < ONLINE_MINUTES) return <Badge status="success" text="Online" />;
    if (minutes < RECENT_MINUTES) return <Badge status="warning" text="Recent" />;
    return <Badge status="default" text="Away" />;
};

const locationText = (r) => {
    const parts = [r.city, r.region, r.country].filter(Boolean);
    if (!parts.length) return <span style={{ color: '#bfbfbf' }}>—</span>;
    return parts.join(', ');
};

const deviceText = (r) => {
    const parts = [r.browser, r.os].filter(Boolean);
    if (!parts.length) return <span style={{ color: '#bfbfbf' }}>—</span>;
    return parts.join(' · ');
};

const relativeTime = (t) => {
    if (!t) return <span style={{ color: '#bfbfbf' }}>—</span>;
    return (
        <Tooltip title={moment(t).format('MMM D, YYYY h:mm:ss A')}>
            <span>{moment(t).fromNow()}</span>
        </Tooltip>
    );
};

const STATUS_TAGS = {
    success: <Tag color="green">success</Tag>,
    failed: <Tag color="red">failed</Tag>,
    unlocked: <Tag color="blue">unlocked</Tag>,
};

const ActiveUsersTab = () => {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [search, setSearch] = useState('');

    const filters = useMemo(() => ({ search: search || undefined }), [search]);
    const { data, loading, error } = useActiveUsers({ page, pageSize, filters });

    const columns = [
        {
            title: 'Status',
            key: 'status',
            width: 110,
            render: (_, r) => activityBadge(r.last_activity_at),
        },
        {
            title: 'User',
            key: 'user',
            render: (_, r) => (
                <Tooltip title={r.username}>
                    <span>{r.name || r.username}</span>
                </Tooltip>
            ),
        },
        { title: 'Company', dataIndex: 'company_name', key: 'company_name', ellipsis: true },
        { title: 'IP', dataIndex: 'ip', key: 'ip', width: 140, render: (v) => v || <span style={{ color: '#bfbfbf' }}>—</span> },
        { title: 'Location', key: 'location', render: (_, r) => locationText(r) },
        { title: 'Device', key: 'device', ellipsis: true, render: (_, r) => deviceText(r) },
        { title: 'Source', dataIndex: 'source', key: 'source', width: 90, render: (v) => v || '' },
        {
            title: 'Last Login',
            dataIndex: 'last_login_at',
            key: 'last_login_at',
            width: 180,
            render: (t) => (t ? moment(t).format('MMM D, YYYY h:mm A') : <span style={{ color: '#bfbfbf' }}>—</span>),
        },
        {
            title: 'Last Activity',
            dataIndex: 'last_activity_at',
            key: 'last_activity_at',
            width: 150,
            render: relativeTime,
        },
    ];

    return (
        <>
            <Space wrap style={{ marginBottom: 12 }}>
                <Input.Search
                    placeholder="Search user or company"
                    allowClear
                    style={{ width: 260 }}
                    onSearch={(v) => { setSearch(v); setPage(1); }}
                />
            </Space>

            {error && <Alert type="error" message={error} style={{ marginBottom: 12 }} />}

            <Table
                className="gh-dash-interview-table"
                columns={columns}
                dataSource={data.rows}
                loading={loading}
                rowKey="user_id"
                size="small"
                pagination={{
                    current: data.page,
                    pageSize: data.pageSize,
                    total: data.total,
                    showSizeChanger: true,
                    pageSizeOptions: ['25', '50', '100'],
                    showTotal: (t) => `${t} users`,
                    onChange: (p, ps) => { setPage(p); setPageSize(ps); },
                }}
            />
        </>
    );
};

const LoginHistoryTab = ({ onUnlocked }) => {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState();
    const [range, setRange] = useState(null);
    const [unlocking, setUnlocking] = useState(null);

    const filters = useMemo(() => ({
        search: search || undefined,
        status,
        from: range && range[0] ? range[0].toISOString() : undefined,
        to: range && range[1] ? range[1].toISOString() : undefined,
    }), [search, status, range]);

    const { data, loading, error, refetch } = useLoginHistory({ page, pageSize, filters });

    const resetToFirst = (setter) => (val) => { setter(val); setPage(1); };

    const handleUnlock = async (username) => {
        setUnlocking(username);
        try {
            await unlockAccount(username);
            message.success(`${username} unlocked`);
            refetch();
            if (onUnlocked) onUnlocked();
        } catch (err) {
            message.error(err.message || 'Failed to unlock account');
        } finally {
            setUnlocking(null);
        }
    };

    const columns = [
        {
            title: 'Time',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 180,
            render: (t) => (t ? moment(t).format('MMM D, YYYY h:mm:ss A') : ''),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (s, r) => {
                if (s === 'locked') {
                    return (
                        <Tooltip title={r.lockout_until ? `Locked until ${moment(r.lockout_until).format('MMM D, h:mm A')}` : 'Account locked'}>
                            <Tag color="orange">locked</Tag>
                        </Tooltip>
                    );
                }
                return STATUS_TAGS[s] || <Tag>{s}</Tag>;
            },
        },
        {
            title: 'Username',
            dataIndex: 'username',
            key: 'username',
            ellipsis: true,
            render: (v, r) => (r.name ? <Tooltip title={r.name}><span>{v}</span></Tooltip> : v),
        },
        { title: 'Company', dataIndex: 'company_name', key: 'company_name', ellipsis: true },
        { title: 'IP', dataIndex: 'ip', key: 'ip', width: 140, render: (v) => v || <span style={{ color: '#bfbfbf' }}>—</span> },
        { title: 'Location', key: 'location', render: (_, r) => locationText(r) },
        { title: 'Device', key: 'device', ellipsis: true, render: (_, r) => deviceText(r) },
        { title: 'Source', dataIndex: 'source', key: 'source', width: 90, render: (v) => v || '' },
        {
            title: '',
            key: 'actions',
            width: 100,
            render: (_, r) => (r.is_active_lock ? (
                <Popconfirm
                    title={`Unlock ${r.username}?`}
                    description="Clears the lockout and resets the failure counters."
                    onConfirm={() => handleUnlock(r.username)}
                    okText="Unlock"
                >
                    <Button
                        size="small"
                        icon={<UnlockOutlined />}
                        loading={unlocking === r.username}
                    >
                        Unlock
                    </Button>
                </Popconfirm>
            ) : null),
        },
    ];

    return (
        <>
            <Space wrap style={{ marginBottom: 12 }}>
                <Input.Search
                    placeholder="Search username, name or IP"
                    allowClear
                    style={{ width: 260 }}
                    onSearch={resetToFirst(setSearch)}
                />
                <Select
                    placeholder="Status"
                    allowClear
                    style={{ minWidth: 130 }}
                    options={[
                        { label: 'Success', value: 'success' },
                        { label: 'Failed', value: 'failed' },
                        { label: 'Locked', value: 'locked' },
                        { label: 'Unlocked', value: 'unlocked' },
                    ]}
                    onChange={resetToFirst(setStatus)}
                />
                <RangePicker showTime onChange={resetToFirst(setRange)} />
            </Space>

            {error && <Alert type="error" message={error} style={{ marginBottom: 12 }} />}

            <Table
                className="gh-dash-interview-table"
                columns={columns}
                dataSource={data.rows}
                loading={loading}
                rowKey="id"
                size="small"
                pagination={{
                    current: data.page,
                    pageSize: data.pageSize,
                    total: data.total,
                    showSizeChanger: true,
                    pageSizeOptions: ['25', '50', '100'],
                    showTotal: (t) => `${t} attempts`,
                    onChange: (p, ps) => { setPage(p); setPageSize(ps); },
                }}
            />
        </>
    );
};

const Sessions = () => {
    const { stats, error: statsError, refetch } = useSecurityStats();

    const tiles = [
        {
            title: 'Active Users (5 min)', value: stats ? stats.activeUsers : '—', icon: <TeamOutlined />, color: '#52c41a',
        },
        {
            title: 'Unique Users Today', value: stats ? stats.uniqueUsersToday : '—', icon: <UserOutlined />, color: '#1677ff',
        },
        {
            title: 'Failed Attempts (24h)', value: stats ? stats.failedAttempts24h : '—', icon: <WarningOutlined />, color: '#faad14',
        },
        {
            title: 'Locked Accounts', value: stats ? stats.lockedAccounts : '—', icon: <LockOutlined />, color: '#ff4d4f',
        },
    ];

    return (
        <div className="gh-users-page" style={{ padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <Title level={4} style={{ margin: 0 }}>Sessions</Title>
                <Button icon={<ReloadOutlined />} onClick={() => refetch()}>Refresh</Button>
            </div>

            {statsError && <Alert type="error" message={statsError} style={{ marginBottom: 12 }} />}

            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                {tiles.map((tile) => (
                    <Col xs={12} md={6} key={tile.title}>
                        <Card className="gh-dash-card" bodyStyle={{ padding: 16 }}>
                            <Statistic
                                title={tile.title}
                                value={tile.value}
                                prefix={React.cloneElement(tile.icon, { style: { color: tile.color } })}
                            />
                        </Card>
                    </Col>
                ))}
            </Row>

            <Card className="gh-dash-card" bodyStyle={{ paddingTop: 8 }}>
                <Tabs
                    defaultActiveKey="active"
                    items={[
                        { key: 'active', label: 'Active Users', children: <ActiveUsersTab /> },
                        { key: 'history', label: 'Login History', children: <LoginHistoryTab onUnlocked={refetch} /> },
                    ]}
                />
            </Card>
        </div>
    );
};

export default Sessions;
